// Compliance Auditor Framework
// ============================================================
// Extensible auditor framework for automotive compliance standards
// Supports: AIAG-VDA, PPAP, IATF 16949, and future standards
// ============================================================

import {
    PfmeaItem,
    ProcessFlowDiagram,
    ControlPlan,
    ProjectScoping,
    ProcessFunction,
    ProductRequirement,
} from '../types';
import { AuditReport, runAiagVdaAudit } from './aiagVdaAuditor';

// ============================================================
// COMPLIANCE STANDARD TYPES
// ============================================================

export type ComplianceStandard =
    | 'AIAG-VDA'
    | 'PPAP'
    | 'IATF-16949'
    | 'ISO-14971'      // Medical device risk management
    | 'ISO-26262'      // Automotive functional safety
    | 'IEC-61508'      // Functional safety
    | 'MIL-STD-1629A'; // Military/Aerospace

export interface StandardDefinition {
    id: ComplianceStandard;
    name: string;
    fullName: string;
    version: string;
    industry: string;
    description: string;
    icon: string;
    color: string;
    requiredElements: string[];
}

// ============================================================
// STANDARD DEFINITIONS DATABASE
// ============================================================

export const COMPLIANCE_STANDARDS: Record<ComplianceStandard, StandardDefinition> = {
    'AIAG-VDA': {
        id: 'AIAG-VDA',
        name: 'AIAG-VDA FMEA',
        fullName: 'AIAG & VDA FMEA Handbook',
        version: '1st Edition, 2019',
        industry: 'Automotive',
        description: 'Harmonized FMEA methodology for North American and European OEMs',
        icon: '🚗',
        color: 'blue',
        requiredElements: [
            '7-Step Process Flow',
            'Structure Analysis',
            'Function Analysis',
            'Failure Analysis',
            'Risk Analysis (AP)',
            'Optimization',
            'Documentation',
        ],
    },
    'PPAP': {
        id: 'PPAP',
        name: 'PPAP',
        fullName: 'Production Part Approval Process',
        version: '4th Edition',
        industry: 'Automotive',
        description: 'Standardized process for confirming that manufacturing process produces quality parts',
        icon: '📋',
        color: 'green',
        requiredElements: [
            'Design Records',
            'Process Flow Diagram',
            'PFMEA',
            'Control Plan',
            'Measurement System Analysis',
            'Initial Process Studies',
            'Part Submission Warrant',
        ],
    },
    'IATF-16949': {
        id: 'IATF-16949',
        name: 'IATF 16949',
        fullName: 'IATF 16949:2016 Quality Management System',
        version: '2016 + SI 2024',
        industry: 'Automotive',
        description: 'International standard for automotive quality management systems',
        icon: '🏭',
        color: 'purple',
        requiredElements: [
            'Quality Management System',
            'Risk-Based Thinking',
            'Product Safety',
            'Process Control',
            'Supplier Management',
            'Continual Improvement',
            'Documented Information',
        ],
    },
    'ISO-14971': {
        id: 'ISO-14971',
        name: 'ISO 14971',
        fullName: 'ISO 14971:2019 Medical Device Risk Management',
        version: '2019',
        industry: 'Medical Device',
        description: 'Risk management process for medical device manufacturers',
        icon: '🏥',
        color: 'teal',
        requiredElements: [
            'Risk Management Plan',
            'Risk Analysis',
            'Risk Evaluation',
            'Risk Control',
            'Residual Risk Evaluation',
            'Risk Management Review',
            'Production & Post-Production',
        ],
    },
    'ISO-26262': {
        id: 'ISO-26262',
        name: 'ISO 26262',
        fullName: 'ISO 26262 Functional Safety',
        version: '2nd Edition, 2018',
        industry: 'Automotive',
        description: 'Functional safety standard for road vehicles',
        icon: '⚡',
        color: 'orange',
        requiredElements: [
            'Safety Management',
            'Concept Phase',
            'Product Development (System/HW/SW)',
            'Production & Operation',
            'Supporting Processes',
            'ASIL Classification',
            'Safety Validation',
        ],
    },
    'IEC-61508': {
        id: 'IEC-61508',
        name: 'IEC 61508',
        fullName: 'IEC 61508 Functional Safety',
        version: 'Edition 2.0, 2010',
        industry: 'Industrial',
        description: 'Functional safety of electrical/electronic systems',
        icon: '⚙️',
        color: 'gray',
        requiredElements: [
            'Safety Lifecycle',
            'Hazard & Risk Analysis',
            'Safety Requirements',
            'Safety Integrity Levels',
            'Validation & Verification',
            'Functional Safety Management',
        ],
    },
    'MIL-STD-1629A': {
        id: 'MIL-STD-1629A',
        name: 'MIL-STD-1629A',
        fullName: 'Military Standard FMECA',
        version: '1980',
        industry: 'Aerospace/Defense',
        description: 'Failure Mode, Effects, and Criticality Analysis procedures',
        icon: '✈️',
        color: 'slate',
        requiredElements: [
            'Functional Block Diagrams',
            'Failure Mode Identification',
            'Failure Effects Analysis',
            'Criticality Analysis',
            'Detection Methods',
            'Compensating Provisions',
        ],
    },
};

// ============================================================
// PPAP ELEMENT CHECKER TYPES
// ============================================================

export interface PpapElementStatus {
    element: string;
    number: number;
    status: 'complete' | 'partial' | 'missing';
    description: string;
    findings: string[];
    recommendations: string[];
}

export interface PpapAuditResult {
    overallReadiness: number;
    status: 'ready' | 'needs-work' | 'not-ready';
    submissionLevel: 1 | 2 | 3 | 4 | 5;
    elements: PpapElementStatus[];
    criticalGaps: string[];
    recommendations: string[];
}

// ============================================================
// PPAP ELEMENT BUILDER HELPERS
// ============================================================

interface PpapContext {
    pfmeaItems: PfmeaItem[];
    processFlow: ProcessFlowDiagram | null;
    controlPlans: ControlPlan[];
    hasDesignRecords: boolean;
    hasMSA: boolean;
    hasSPC: boolean;
}

function buildDesignRecordsElement(ctx: PpapContext): PpapElementStatus {
    return {
        element: 'Design Records', number: 1,
        status: ctx.hasDesignRecords ? 'complete' : 'missing',
        description: 'Engineering drawings, CAD data, and specifications',
        findings: ctx.hasDesignRecords ? [] : ['No design records linked to project'],
        recommendations: ctx.hasDesignRecords ? [] : ['Link design records via Linked Data page'],
    };
}

function buildProcessFlowElement(ctx: PpapContext): PpapElementStatus {
    const hasPfd = ctx.processFlow && (ctx.processFlow.steps?.length ?? 0) > 0;
    return {
        element: 'Process Flow Diagram', number: 4,
        status: hasPfd ? 'complete' : 'missing',
        description: 'Shows all manufacturing steps and material flow',
        findings: hasPfd ? [`Process flow has ${ctx.processFlow?.steps?.length ?? 0} steps defined`] : ['No Process Flow Diagram found'],
        recommendations: hasPfd ? [] : ['Create Process Flow Diagram in Structure Analysis'],
    };
}

function buildPfmeaElement(ctx: PpapContext): PpapElementStatus {
    const hasPfmea = ctx.pfmeaItems.length > 0;
    const pfmeaComplete = ctx.pfmeaItems.filter(i =>
        i.severity && i.occurrence && i.detection && i.failureMode && i.failureEffectEnd && i.failureCause
    ).length;
    const status = !hasPfmea ? 'missing' : pfmeaComplete === ctx.pfmeaItems.length ? 'complete' : 'partial';
    return {
        element: 'Process FMEA', number: 7, status,
        description: 'Identifies potential process failure modes and controls',
        findings: hasPfmea ? [`${pfmeaComplete} of ${ctx.pfmeaItems.length} items fully documented`] : ['No PFMEA items found'],
        recommendations: status === 'complete' ? [] : ['Complete all PFMEA fields for each failure mode', 'Ensure S/O/D ratings are assigned'],
    };
}

function buildControlPlanElement(ctx: PpapContext): PpapElementStatus {
    const hasControlPlan = ctx.controlPlans.length > 0 && ctx.controlPlans[0]?.items?.length > 0;
    const cpItemCount = hasControlPlan ? ctx.controlPlans[0].items.length : 0;
    const status = hasControlPlan ? (cpItemCount >= ctx.pfmeaItems.length ? 'complete' : 'partial') : 'missing';
    return {
        element: 'Control Plan', number: 8, status,
        description: 'Documents control methods for each process characteristic',
        findings: hasControlPlan ? [`Control Plan has ${cpItemCount} items`] : ['No Control Plan found'],
        recommendations: !hasControlPlan
            ? ['Generate Control Plan from PFMEA']
            : cpItemCount < ctx.pfmeaItems.length ? ['Add Control Plan items for all process characteristics'] : [],
    };
}

function buildMsaElement(ctx: PpapContext): PpapElementStatus {
    return {
        element: 'Measurement System Analysis', number: 9,
        status: ctx.hasMSA ? 'complete' : 'missing',
        description: 'Gage R&R studies for measurement equipment',
        findings: ctx.hasMSA ? [] : ['MSA records not linked'],
        recommendations: ctx.hasMSA ? [] : ['Conduct Gage R&R studies and link results'],
    };
}

function buildSpcElement(ctx: PpapContext): PpapElementStatus {
    return {
        element: 'Initial Process Studies', number: 10,
        status: ctx.hasSPC ? 'complete' : 'missing',
        description: 'Capability studies (Ppk/Cpk) for critical characteristics',
        findings: ctx.hasSPC ? [] : ['SPC/Capability data not linked'],
        recommendations: ctx.hasSPC ? [] : ['Conduct capability studies for CC/SC characteristics'],
    };
}

// ============================================================
// PPAP COMPLIANCE AUDIT
// ============================================================
function auditPpapCompliance(
    pfmeaItems: PfmeaItem[],
    processFlow: ProcessFlowDiagram | null,
    controlPlans: ControlPlan[],
    hasDesignRecords: boolean = false,
    hasMSA: boolean = false,
    hasSPC: boolean = false,
): PpapAuditResult {
    const ctx: PpapContext = { pfmeaItems, processFlow, controlPlans, hasDesignRecords, hasMSA, hasSPC };
    const elements: PpapElementStatus[] = [
        buildDesignRecordsElement(ctx),
        buildProcessFlowElement(ctx),
        buildPfmeaElement(ctx),
        buildControlPlanElement(ctx),
        buildMsaElement(ctx),
        buildSpcElement(ctx),
    ];

    const criticalGaps: string[] = [];
    const hasPfd = processFlow && (processFlow.steps?.length ?? 0) > 0;
    const hasPfmea = pfmeaItems.length > 0;
    const hasControlPlan = controlPlans.length > 0 && controlPlans[0]?.items?.length > 0;

    if (!hasPfd) criticalGaps.push('Missing Process Flow Diagram (Element 4)');
    if (!hasPfmea) criticalGaps.push('Missing PFMEA (Element 7)');
    if (!hasControlPlan) criticalGaps.push('Missing Control Plan (Element 8)');

    const completeCount = elements.filter(e => e.status === 'complete').length;
    const partialCount = elements.filter(e => e.status === 'partial').length;
    const overallReadiness = Math.round(((completeCount + partialCount * 0.5) / elements.length) * 100);

    let submissionLevel: 1 | 2 | 3 | 4 | 5 = 3;
    if (criticalGaps.length > 0) submissionLevel = 5;
    else if (overallReadiness < 50) submissionLevel = 4;
    else if (overallReadiness >= 90) submissionLevel = 1;

    return {
        overallReadiness,
        status: overallReadiness >= 80 ? 'ready' : overallReadiness >= 50 ? 'needs-work' : 'not-ready',
        submissionLevel,
        elements,
        criticalGaps,
        recommendations: criticalGaps.map(g => `Address: ${g}`),
    };
}


// ============================================================
// IATF 16949 CHECKER
// ============================================================

export interface IatfClauseStatus {
    clause: string;
    number: string;
    status: 'conforming' | 'minor-nc' | 'major-nc' | 'not-assessed';
    description: string;
    findings: string[];
    opportunities: string[];
}

export interface IatfAuditResult {
    overallScore: number;
    status: 'conforming' | 'minor-nc' | 'major-nc';
    clauses: IatfClauseStatus[];
    majorNonconformances: string[];
    minorNonconformances: string[];
    opportunities: string[];
}

function auditIatf16949(
    pfmeaItems: PfmeaItem[],
    processFlow: ProcessFlowDiagram | null,
    controlPlans: ControlPlan[],
    _aiagVdaReport: AuditReport,
): IatfAuditResult {
    const clauses: IatfClauseStatus[] = [];
    const majorNCs: string[] = [];
    const minorNCs: string[] = [];
    const opportunities: string[] = [];

    // Clause 4.4 - QMS and its processes
    const hasProcessFlow = processFlow && (processFlow.steps?.length ?? 0) > 0;
    const processFlowComplete = !!hasProcessFlow && (processFlow?.steps?.every(s => s.stepNumber && s.stepName) ?? false);
    clauses.push({
        clause: 'QMS and its processes',
        number: '4.4',
        status: processFlowComplete ? 'conforming' : hasProcessFlow ? 'minor-nc' : 'major-nc',
        description: 'Establish, implement, maintain, and improve QMS processes',
        findings: processFlowComplete
            ? [`Process flow established with ${processFlow?.steps?.length ?? 0} fully defined steps`]
            : hasProcessFlow
                ? ['Process flow exists but some steps are missing details']
                : ['Manufacturing processes not documented'],
        opportunities: processFlowComplete ? [] : ['Ensure all process steps have clear names and numbers'],
    });

    // Clause 6.1 - Risk-based thinking
    // STRICTER: Check for completeness of risk analysis, not just existence
    const hasRiskAnalysis = pfmeaItems.length > 0;
    const itemsWithRatings = pfmeaItems.filter(i => i.severity > 0 && i.occurrence > 0 && i.detection > 0).length;
    const riskAnalysisComplete = hasRiskAnalysis && (itemsWithRatings / pfmeaItems.length) >= 0.9;

    clauses.push({
        clause: 'Actions to address risks and opportunities',
        number: '6.1',
        status: riskAnalysisComplete ? 'conforming' : hasRiskAnalysis ? 'minor-nc' : 'major-nc',
        description: 'Determine risks and opportunities; plan actions',
        findings: riskAnalysisComplete
            ? [`Robust risk analysis: ${itemsWithRatings} of ${pfmeaItems.length} items fully rated`]
            : hasRiskAnalysis
                ? [`Risk analysis incomplete: Only ${itemsWithRatings} of ${pfmeaItems.length} items rated`]
                : ['No risk analysis (PFMEA) performed'],
        opportunities: riskAnalysisComplete ? [] : ['Complete S/O/D ratings for all failure modes'],
    });

    // Clause 8.3.3.3 - Special characteristics
    const ccItems = pfmeaItems.filter(i => i.specialCharacteristic === 'CC');
    const scItems = pfmeaItems.filter(i => i.specialCharacteristic === 'SC');
    const hasSpecialChars = ccItems.length > 0 || scItems.length > 0;
    // If no special chars, audit if they were looked for (e.g. unclassified count)
    const analyzedForSpecialChars = pfmeaItems.every(i => i.specialCharacteristic !== undefined);

    clauses.push({
        clause: 'Special characteristics',
        number: '8.3.3.3',
        status: hasSpecialChars ? 'conforming' : analyzedForSpecialChars ? 'minor-nc' : 'major-nc',
        description: 'Identify special characteristics with customer designations',
        findings: hasSpecialChars
            ? [`${ccItems.length} CC and ${scItems.length} SC identified`]
            : ['No special characteristics designated'],
        opportunities: hasSpecialChars
            ? []
            : ['Review PFMEA and designate CC/SC for safety and critical characteristics'],
    });

    // Clause 8.5.1.1 - Control Plan
    const hasControlPlan = controlPlans.length > 0 && controlPlans[0]?.items?.length > 0;
    const cpItemCount = hasControlPlan ? controlPlans[0].items.length : 0;
    // Rule of thumb: CP should cover most PFMEA steps
    const cpCoverage = pfmeaItems.length > 0 ? cpItemCount / pfmeaItems.length : 0;
    const cpConforming = cpCoverage >= 0.8;

    clauses.push({
        clause: 'Control Plan',
        number: '8.5.1.1',
        status: cpConforming ? 'conforming' : hasControlPlan ? 'minor-nc' : 'major-nc',
        description: 'Develop control plans covering all process controls',
        findings: cpConforming
            ? [`Control Plan comprehensive: ${cpItemCount} items covering >80% of PFMEA`]
            : hasControlPlan
                ? [`Control Plan coverage low: ${cpItemCount} items for ${pfmeaItems.length} PFMEA items`]
                : ['Control Plan not developed'],
        opportunities: cpConforming
            ? []
            : ['Expand Control Plan to cover all process steps and characteristics'],
    });

    // Clause 8.5.6.1 - Control of changes
    const approvedItems = pfmeaItems.filter(i => i.status === 'approved').length;
    const approvalRate = pfmeaItems.length > 0 ? approvedItems / pfmeaItems.length : 0;
    const changesControlled = approvalRate >= 0.8;

    clauses.push({
        clause: 'Control of changes',
        number: '8.5.6.1',
        status: changesControlled ? 'conforming' : approvalRate > 0 ? 'minor-nc' : 'major-nc',
        description: 'Control and review changes to processes',
        findings: changesControlled
            ? [`Change control active: ${approvedItems} of ${pfmeaItems.length} items approved`]
            : ['Formal approval workflow pending for most items'],
        opportunities: changesControlled
            ? []
            : ['Approve all PFMEA items through the change control workflow'],
    });

    // Clause 10.2.3 - Problem solving
    const highPriorityItems = pfmeaItems.filter(i => i.actionPriority === 'H');
    const highPriorityWithActions = highPriorityItems.filter(i => i.recommendedActions?.length > 0);
    const problemSolvingEffective = highPriorityItems.length === 0 || highPriorityWithActions.length === highPriorityItems.length;

    clauses.push({
        clause: 'Problem solving',
        number: '10.2.3',
        status: problemSolvingEffective ? 'conforming' : 'major-nc',
        description: 'Documented problem-solving processes',
        findings: problemSolvingEffective
            ? ['All High priority risks have recommended actions defined']
            : [`${highPriorityItems.length - highPriorityWithActions.length} High priority risks missing actions`],
        opportunities: problemSolvingEffective
            ? []
            : ['Define corrective actions for all High priority risks immediately'],
    });

    // Compile results
    clauses.forEach(c => {
        if (c.status === 'major-nc') majorNCs.push(`${c.number}: ${c.clause}`);
        if (c.status === 'minor-nc') minorNCs.push(`${c.number}: ${c.clause}`);
        if (c.opportunities.length > 0) opportunities.push(...c.opportunities);
    });

    const conformingCount = clauses.filter(c => c.status === 'conforming').length;
    const overallScore = Math.round((conformingCount / clauses.length) * 100);

    return {
        overallScore,
        status: majorNCs.length > 0 ? 'major-nc' : minorNCs.length > 0 ? 'minor-nc' : 'conforming',
        clauses,
        majorNonconformances: majorNCs,
        minorNonconformances: minorNCs,
        opportunities,
    };
}

// ============================================================
// COMPREHENSIVE COMPLIANCE AUDIT
// ============================================================

export interface ComprehensiveAuditResult {
    generatedAt: Date;
    projectName: string;

    // Individual standard results
    aiagVda: AuditReport;
    ppap: PpapAuditResult;
    iatf16949: IatfAuditResult;

    // Overall summary
    overallCompliance: number;          // 0-100
    overallStatus: 'compliant' | 'needs-work' | 'non-compliant';
    certificationReady: boolean;

    // Prioritized findings
    criticalActions: string[];
    highPriorityActions: string[];
    improvements: string[];

    // Executive summary
    executiveSummary: string;
    nextSteps: string[];
}

export function runComprehensiveAudit(
    projectName: string,
    pfmeaItems: PfmeaItem[],
    processFlow: ProcessFlowDiagram | null,
    controlPlans: ControlPlan[],
    scoping?: ProjectScoping,
    functions: ProcessFunction[] = [],
    requirements: ProductRequirement[] = [],
    _projectInfo: unknown = null,
): ComprehensiveAuditResult {
    // Run AIAG-VDA Audit
    const aiagVdaResult = runAiagVdaAudit(
        pfmeaItems,
        processFlow,
        controlPlans,
        projectName,
        scoping,
        functions,
        requirements,
    );

    // Run PPAP Audit
    const ppapResult = auditPpapCompliance(
        pfmeaItems,
        processFlow,
        controlPlans,
        false, // hasDesignRecords - would come from project settings
        false, // hasMSA
        false, // hasSPC
    );

    // Run IATF 16949 Audit
    const iatfResult = auditIatf16949(
        pfmeaItems,
        processFlow,
        controlPlans,
        aiagVdaResult,
    );

    // Calculate overall compliance
    const overallCompliance = Math.round(
        (aiagVdaResult.overallScore * 0.4) +
        (ppapResult.overallReadiness * 0.3) +
        (iatfResult.overallScore * 0.3)
    );

    // Determine overall status
    const overallStatus: 'compliant' | 'needs-work' | 'non-compliant' =
        overallCompliance >= 80 ? 'compliant' :
            overallCompliance >= 50 ? 'needs-work' : 'non-compliant';

    // Compile critical actions
    const criticalActions: string[] = [
        ...aiagVdaResult.criticalFindings.map(f => f.recommendation),
        ...ppapResult.criticalGaps,
        ...iatfResult.majorNonconformances.map(nc => `Address IATF NC: ${nc}`),
    ];

    // Compile high priority actions
    const highPriorityActions: string[] = [
        ...aiagVdaResult.majorFindings.map(f => f.recommendation),
        ...iatfResult.minorNonconformances.map(nc => `Resolve: ${nc}`),
    ];

    // Compile improvements
    const improvements: string[] = [
        ...aiagVdaResult.recommendations.slice(0, 5),
        ...iatfResult.opportunities.slice(0, 5),
    ];

    // Generate executive summary
    const executiveSummary = generateExecutiveSummary(
        overallCompliance,
        aiagVdaResult,
        ppapResult,
        iatfResult,
    );

    // Generate next steps
    const nextSteps = generateNextSteps(
        aiagVdaResult,
        ppapResult,
        iatfResult,
    );

    return {
        generatedAt: new Date(),
        projectName,
        aiagVda: aiagVdaResult,
        ppap: ppapResult,
        iatf16949: iatfResult,
        overallCompliance,
        overallStatus,
        certificationReady: overallCompliance >= 80 && criticalActions.length === 0,
        criticalActions,
        highPriorityActions,
        improvements,
        executiveSummary,
        nextSteps,
    };
}

function generateExecutiveSummary(
    overallCompliance: number,
    aiagVda: AuditReport,
    ppap: PpapAuditResult,
    iatf: IatfAuditResult,
): string {
    if (overallCompliance >= 90) {
        return `Excellent! Your PFMEA is ${overallCompliance}% compliant with automotive standards. ` +
            `AIAG-VDA methodology is well-implemented, PPAP elements are ${ppap.status}, ` +
            `and IATF 16949 requirements are largely satisfied. Minor improvements can further strengthen compliance.`;
    }

    if (overallCompliance >= 70) {
        return `Good progress! Your PFMEA is ${overallCompliance}% compliant. ` +
            `AIAG-VDA score: ${aiagVda.overallScore}%, PPAP readiness: ${ppap.overallReadiness}%. ` +
            `Focus on addressing high-priority gaps to achieve certification readiness.`;
    }

    if (overallCompliance >= 50) {
        return `Your PFMEA needs work to meet automotive compliance. Current score: ${overallCompliance}%. ` +
            `Key gaps include: ${aiagVda.criticalFindings.length} critical AIAG-VDA findings, ` +
            `${ppap.criticalGaps.length} PPAP gaps, and ${iatf.majorNonconformances.length} IATF major NCs. ` +
            `Address critical issues before customer audits or PPAP submission.`;
    }

    return `Significant work required. Your PFMEA compliance is at ${overallCompliance}%. ` +
        `The PFMEA methodology has not been fully implemented. ` +
        `Use the AI tools to generate initial content and follow the 7-Step workflow to build a compliant PFMEA.`;
}

function generateNextSteps(
    aiagVda: AuditReport,
    ppap: PpapAuditResult,
    iatf: IatfAuditResult,
): string[] {
    const steps: string[] = [];

    // AIAG-VDA steps
    const incompleteSteps = aiagVda.stepResults.filter(s => s.status !== 'compliant');
    if (incompleteSteps.length > 0) {
        const lowestStep = incompleteSteps[0];
        steps.push(`Complete AIAG-VDA Step ${lowestStep.step}: ${lowestStep.stepName} (${lowestStep.score}% complete)`);
    }

    // PPAP steps
    if (ppap.criticalGaps.length > 0) {
        steps.push(`Address PPAP gap: ${ppap.criticalGaps[0]}`);
    }

    // IATF steps
    if (iatf.majorNonconformances.length > 0) {
        steps.push(`Resolve IATF major NC: ${iatf.majorNonconformances[0]}`);
    }

    // General improvements
    if (aiagVda.recommendations.length > 0) {
        steps.push(aiagVda.recommendations[0]);
    }

    // Always include these if not already addressed
    if (!steps.some(s => s.toLowerCase().includes('control plan'))) {
        const cpStatus = ppap.elements.find(e => e.element === 'Control Plan');
        if (cpStatus?.status !== 'complete') {
            steps.push('Generate Control Plan from PFMEA to complete PPAP documentation');
        }
    }

    return steps.slice(0, 5); // Limit to 5 next steps
}

export default {
    COMPLIANCE_STANDARDS,
    runComprehensiveAudit,
};
